import Phaser from "phaser";
import { VEHICLES, type VehicleDef, vehicleById, MAX_PER_TYPE } from "../vehicles";
import { sfx } from "../sfx";

const W = 1920;
const H = 1080;
const SCENE_DURATION_MS = 120_000;       // 2 min
const SPRITE_TARGET_HEIGHT = 200;        // px on the 1920x1080 canvas

// Visual y-coordinates calibrated against the generated bg_landscape.png:
// grass-road boundary ≈ 0.78H, road runs ≈ 0.84-1.0H. Building sits at the
// boundary; vehicles drive on the road; kids stand on the grass strip.
const HORIZON_Y = H * 0.78;   // building bottom anchors here
const KID_Y     = H * 0.81;   // injured kids on grass just in front of building
const LANE_Y    = H * 0.91;   // vehicle lane (middle of the road)

// Anything that moves around as a vehicle in the scene — Image (when sprite_url
// is set on the catalog entry) or Text (emoji fallback). Both have the
// transform/visual props we need.
type VehicleObj = Phaser.GameObjects.Image | Phaser.GameObjects.Text;

export class FireScene extends Phaser.Scene {
  private fireIntensity   = 100;          // 0..100
  private injuredEmojis: Phaser.GameObjects.Text[] = [];
  private flameEmojis:   Phaser.GameObjects.Text[] = [];

  private alive: Map<string, number> = new Map();
  private timerText!: Phaser.GameObjects.Text;
  private intensityText!: Phaser.GameObjects.Text;
  private endsAt = 0;

  // Public callback so main.ts can react when scene ends (rotate scene etc.)
  onSceneEnd?: () => void;

  constructor() { super("FireScene"); }

  preload() {
    // Load every vehicle that has a sprite. Phaser caches by key so reloading
    // when the scene restarts is idempotent.
    for (const v of Object.values(VEHICLES)) {
      if (v.sprite_url && !this.textures.exists(v.id)) {
        this.load.image(v.id, v.sprite_url);
      }
    }
    // Scene assets — backdrop, building, hurt kid sprite. All Pixar-toy
    // styled by Gemini 2.5 Flash Image to match the vehicle fleet.
    if (!this.textures.exists("scene_bg"))         this.load.image("scene_bg",         "/sprites/bg_landscape.png");
    if (!this.textures.exists("building_burning")) this.load.image("building_burning", "/sprites/building_burning.png");
    if (!this.textures.exists("injured_kid"))      this.load.image("injured_kid",      "/sprites/injured_kid.png");
  }

  create() {
    // Backdrop — generated Pixar-toy landscape stretched edge to edge.
    // The image is roughly horizon-at-65% so it lines up with the lane
    // we use for vehicles below.
    const bg = this.add.image(W / 2, H / 2, "scene_bg");
    bg.setDisplaySize(W, H);

    // Building — single sprite replaces the old gray rectangle + window
    // grid. setOrigin(0.5, 1) anchors the bottom of the image to the
    // grass-road boundary (HORIZON_Y) so the kid lane sits just in front.
    const buildingX = W * 0.5;
    const buildingTargetH = 540;
    const building = this.add.image(buildingX, HORIZON_Y, "building_burning")
      .setOrigin(0.5, 1);
    building.setScale(buildingTargetH / building.height);

    // Flames on top of the chimney/roof — kept as emoji so the per-flame
    // wiggle stays cheap and lively. flames sit just above the building.
    const buildingTopY = HORIZON_Y - building.displayHeight;
    for (let i = 0; i < 5; i++) {
      const fx = buildingX - building.displayWidth / 2 + 30 + i * (building.displayWidth - 60) / 4;
      const f = this.add.text(fx, buildingTopY - 12, "🔥", { fontSize: "56px" }).setOrigin(0.5, 1);
      this.tweens.add({
        targets: f, scaleY: 1.15, scaleX: 1.05, duration: 380,
        yoyo: true, repeat: -1, ease: "Sine.easeInOut", delay: i * 80,
      });
      this.flameEmojis.push(f as unknown as Phaser.GameObjects.Text);
    }

    // Injured kids on the ground next to the building — generated sprite
    // in place of 🤕 emoji. We still keep them in `injuredEmojis` since
    // Phaser.GameObjects.Image / .Text share enough surface for our use
    // (alpha tween, destroy, x position).
    for (let i = 0; i < 3; i++) {
      const px = buildingX - 260 + i * 170;
      const py = KID_Y;
      const kid = this.add.image(px, py, "injured_kid").setOrigin(0.5, 1);
      const kidTargetH = 130;
      kid.setScale(kidTargetH / kid.height);
      // Stored as Text-typed list to keep the existing run-time API; the
      // shared GameObject props (x, y, alpha, destroy) are all we touch.
      this.injuredEmojis.push(kid as unknown as Phaser.GameObjects.Text);
    }

    // HUD inside scene — strokes added so text stays legible against the
    // bright sky in the new bg landscape.
    const hudStroke = { stroke: "#0b1426", strokeThickness: 6 };
    this.intensityText = this.add.text(40, 40, "", {
      fontSize: "36px", color: "#ff7b7b", fontStyle: "bold", ...hudStroke,
    });
    this.timerText = this.add.text(W - 40, 40, "", {
      fontSize: "36px", color: "#ffe5a0", fontStyle: "bold", ...hudStroke,
    }).setOrigin(1, 0);
    this.add.text(W / 2, 40, "🔥 BUILDING ON FIRE — send help! 🔥", {
      fontSize: "40px", color: "#ffffff", fontStyle: "bold", ...hudStroke,
    }).setOrigin(0.5, 0);

    this.endsAt = this.time.now + SCENE_DURATION_MS;
    this.refreshHud();

    // Heartbeat so we can see in vroom-web logs that the scene's render
    // loop is actually running (vs. canvas alive but stuck).
    this.time.addEvent({
      delay: 3000, repeat: -1,
      callback: () => console.log(
        `[scene] alive t=${Math.round(this.time.now)}ms intensity=${Math.round(this.fireIntensity)}% alive_vehicles=${this.alive.size}`
      ),
    });
  }

  update() {
    this.refreshHud();
    if (this.time.now >= this.endsAt) {
      this.endsAt = Number.POSITIVE_INFINITY;     // fire once
      this.cameras.main.fade(800, 0, 0, 0);
      this.time.delayedCall(900, () => this.onSceneEnd?.());
    }
  }

  private refreshHud() {
    const remainingMs = Math.max(0, this.endsAt - this.time.now);
    const m = Math.floor(remainingMs / 60000);
    const s = Math.floor((remainingMs % 60000) / 1000).toString().padStart(2, "0");
    this.timerText.setText(`⏱ ${m}:${s}`);
    this.intensityText.setText(`🔥 ${Math.round(this.fireIntensity)}%   🤕 ${this.injuredEmojis.length}`);

    // sync visible flames to intensity
    const wantFlames = Math.ceil((this.fireIntensity / 100) * 5);
    this.flameEmojis.forEach((f, i) => f.setVisible(i < wantFlames));
  }

  // ============================================================
  // Spawn vehicle for a tap
  // ============================================================
  spawnVehicle(def: VehicleDef): boolean {
    const count = this.alive.get(def.id) ?? 0;
    if (count >= MAX_PER_TYPE) return false;
    this.alive.set(def.id, count + 1);

    sfx.vehicle(def.id);

    const startX = W + 120;
    const laneY  = LANE_Y;               // ride along the road in the bg landscape
    const sprite = this.makeVehicle(def, startX, laneY);

    // Subtle "alive" motion — only for image sprites. Emoji already has lots of
    // built-in personality and bouncing the text looks janky.
    if (sprite.type === "Image") {
      this.tweens.add({
        targets: sprite, y: laneY - 4,
        duration: 220, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });
      this.tweens.add({
        targets: sprite, angle: -1.5,
        duration: 320, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });
    }

    const onDone = () => {
      sprite.destroy();
      this.alive.set(def.id, Math.max(0, (this.alive.get(def.id) ?? 1) - 1));
    };

    switch (def.job) {
      case "extinguish": this.runExtinguish(sprite, def, onDone); break;
      case "rescue":     this.runRescue(sprite, def, onDone);     break;
      default:           this.runDriveBy(sprite, def, onDone);    break;
    }
    return true;
  }

  // Build the vehicle visual: prefer the image sprite, fall back to emoji text.
  private makeVehicle(def: VehicleDef, x: number, y: number): VehicleObj {
    if (def.sprite_url && this.textures.exists(def.id)) {
      const img = this.add.image(x, y, def.id).setOrigin(0.5, 1);
      // Normalize to the same on-screen height regardless of source resolution.
      img.setScale((SPRITE_TARGET_HEIGHT / img.height) * def.scale);
      return img;
    }
    return this.add.text(x, y, def.emoji, { fontSize: "120px" })
      .setOrigin(0.5, 1)
      .setScale(def.scale);
  }

  // ============================================================
  // Behaviors
  // ============================================================
  private runExtinguish(sprite: VehicleObj, def: VehicleDef, done: () => void) {
    const stopX = W * 0.5 + 240;
    this.driveTo(sprite, stopX, def.speed, () => {
      // spray for 1.5s, then chip away at the fire
      const sprayTimer = this.time.addEvent({
        delay: 70, repeat: 20, callback: () => {
          this.spawnDroplet(sprite.x, sprite.y - 80);
          sfx.waterSpray();
        },
      });
      this.tweens.add({
        targets: this, fireIntensity: Math.max(0, this.fireIntensity - 18),
        duration: 1500, ease: "Linear",
      });
      this.time.delayedCall(1700, () => {
        sprayTimer.remove();
        this.driveTo(sprite, -200, def.speed, done);
      });
    });
  }

  private runRescue(sprite: VehicleObj, def: VehicleDef, done: () => void) {
    const target = this.injuredEmojis.shift();
    if (!target) {
      // no one to rescue → just drive through
      this.driveTo(sprite, -200, def.speed, done);
      return;
    }
    const stopX = target.x;
    this.driveTo(sprite, stopX, def.speed, () => {
      sfx.rescueDing();
      this.tweens.add({
        targets: target, alpha: 0, y: target.y - 30, duration: 350,
        onComplete: () => target.destroy(),
      });
      this.time.delayedCall(450, () => this.driveTo(sprite, -200, def.speed, done));
    });
  }

  private runDriveBy(sprite: VehicleObj, def: VehicleDef, done: () => void) {
    this.driveTo(sprite, -200, def.speed, done);
  }

  // ============================================================
  // Helpers
  // ============================================================
  private driveTo(
    sprite: VehicleObj,
    targetX: number,
    speedPx: number,
    onDone: () => void,
  ) {
    const dist = Math.abs(sprite.x - targetX);
    this.tweens.add({
      targets: sprite, x: targetX, duration: (dist / speedPx) * 1000,
      ease: "Linear", onComplete: onDone,
    });
  }

  private spawnDroplet(x: number, y: number) {
    const d = this.add.text(x, y, "💧", { fontSize: "32px" }).setOrigin(0.5, 0.5);
    this.tweens.add({
      targets: d, x: x + (W * 0.5 - x) * 0.6, y: y + 20, alpha: 0,
      duration: 600, onComplete: () => d.destroy(),
    });
  }
}
